import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useController } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    Grid,
    Box,
    Typography,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { CloseIcon } from '../../../assets';
import { CustomReactSelect, CustomButton, CustomSwitch } from '../../shared';
import {
    getCampaignTriggers,
    createCampaign,
    getCampaignDetails,
    getCount,
    getCampaignSummary,
    updateCampaign,
    getAllCampaigns
} from '../../../redux/thunks/campaignThunks';
import { selectCampaignTriggerOptions,
    getTargetDurationOptions,
    getEstimatedDurationOptions,
} from '../../../redux/reducers/campaignReducer';
import { daysOptions, hourlyDropdownOptions, validateForScriptTag } from '../../../utility';
import { getImportance, getIntensity } from '../../../redux/thunks/physiciansThunk';
import { actions as algorithmCampaignActions, createDecileData, createIntensityAndCount, createSegmentData} from '../../../redux/reducers/algorithemCampaignReducer';
import { DUAL_DECILE_TABLE_COLUMNS } from '../physicians/decilesAndSegments/constants';
import { daysOrder } from '../../../constants';

const ROWS = [
    {
        deciles: 'Total',
        quantity: '0',
        importance: '',
    },
];

const initialValue =  {
    trigger: '', 
    campaignName: '',
    targetingDuration: '',
    estimatedDuration: '',
    day: 'Monday',
    time: 1,
    autoScheduler: false,
};

const campaignAlgorithm = yup
    .object({
        trigger: yup.array()
            .min(1, 'Please Select at least one Trigger')
            .required('Please Select Trigger'),
        campaignName: yup.string().required('Campaign Name is required').required('Campaign Name is required').test('no-script-tag', 'Script tags are not allowed.', validateForScriptTag),
        targetingDuration: yup
            .string()
            .required('Please Select Targeting Duration'),
        estimatedDuration: yup
            .string()
            .required('Please Select Estimated Duration'),
        day: yup.string().required('Please Select Day'),
        time: yup.string().required('Please Select Time'),
        autoScheduler: yup.boolean().default(false),
    })
    .required();

function CreateAlgorithmCampaign(props) {
    const { handleClose, campaignId } = props;
    const dispatch = useDispatch();
    const targetingDurations = useSelector(getTargetDurationOptions);
    const estimatedDurations = useSelector(getEstimatedDurationOptions);
    const { company } = useSelector((state) => state.company);
    const { campaign } = useSelector((state) => state.campaign);
    const { dualDeciles, decileAndSegmentData } = useSelector(state => state.physician)
    const { counts, campaignsummary } = useSelector(state => state.algorithmCampaign);   
    const campaignTriggers = useSelector(selectCampaignTriggerOptions());

    const { register, handleSubmit, setValue, getValues, formState: { errors }, control, } = useForm({ resolver: yupResolver(campaignAlgorithm), defaultValues: initialValue });
    
    const { field: { value: trigger, onChange: onTriggerChange, ...restTriggerProps }, } = useController({ name: 'trigger', control });
    const { field: { value: targetingDuration, onChange: onTargetingDurationChange, ...restTargetingDurationProps }, } = useController({ name: 'targetingDuration', control });
    const { field: { value: estimatedDuration, onChange: onEstimatedDurationChange, ...restEstimatedDurationProps }, } = useController({ name: 'estimatedDuration', control });
    const { field: { value: day, onChange: onDayChange, ...restDayProps }, } = useController({ name: 'day', control });
    const { field: { value: time, onChange: onTimeChange, ...restTimeProps }, } = useController({ name: 'time', control });
    const { field: { value: autoScheduler, onChange: onAutoSchedulerChange, ...restAutoSchedulerProps }, } = useController({ name: 'autoScheduler', control });
 
    const [dualDecile, setDualDecile] = useState(false);
    const [summary, setSummary] = useState(false);
    const [decileAndSegment, setDecileAndSegment] = useState(false);
    const [decileData, setDecileData] = useState([]);
    const [dualDecileData, setDualDecileData] = useState([]);
    const [segmentData, setSegmentData] = useState([]);
    const handleShowDualDecile = () => {
        setDualDecile(!dualDecile);
    };
    const handleShowDualSummary = () => {
        setSummary(!summary);
    };

    const handleShowDecileAndSegment = () => {
        setDecileAndSegment(!decileAndSegment);
    };

    const onSubmit = (data) => {
        
        let createCampaignData = {
            auto_scheduler: data.autoScheduler,
            estimated_duration: data.estimatedDuration,
            hour: data.time,
            name: data.campaignName,
            partner_id: "",
            schedule: data.day,
            targeted_duration: data.targetingDuration,
            triggers: data.trigger?.map(item =>  item.id),
            type: "",
        };
        if(campaignId){
            dispatch(updateCampaign({companyId: company.id,  campaignId: campaignId, data: createCampaignData})).then(result => {
                if(result?.payload?.status_code=== 200){
                    dispatch(getAllCampaigns({ companyId: company.id }))
                    dispatch(getCount({companyId: company.id, campaignId: campaignId}));
                }
            });
        }
        else{
            dispatch(createCampaign({companyId: company.id, data: createCampaignData})).then(result => {
                if(result?.payload?.status_code=== 200){
                    dispatch(getCount({companyId: company.id, campaignId: result.payload.campaign.id}));
                }
            });
        }
        
    };

    const handleSave = () => {
        handleSubmit(onSubmit);
    };

    const handleTriggerChange = (selectedOption, actionMeta) => {
        const currentTriggerValue = getValues('trigger');
        if (actionMeta.action === 'remove-value' || actionMeta.action === 'pop-value') {
            const removedOption = actionMeta.removedValue;
            const indexToRemove = currentTriggerValue.findIndex(item => item.id === removedOption.id);

            if (indexToRemove !== -1) {
                currentTriggerValue.splice(indexToRemove, 1);
                setValue('trigger', [...currentTriggerValue]);
            }
        
        } else{
            setValue('trigger', [...selectedOption]);
        }
    };

    const getCampaignGenerationTypeData = () => {
        if (company.id) {
            dispatch(getCampaignTriggers({ companyId: company.id }));
        }
    };

    useEffect(() => {
        if(campaignId){
            dispatch(getCampaignDetails({companyId: company.id, campaignId: campaignId}));
            dispatch(getCount({companyId: company.id, campaignId: campaignId}));
            dispatch(getIntensity({companyId: company.id}));
            dispatch(getImportance({companyId: company.id}))
            dispatch(getCampaignSummary({companyId: company.id, campaignId: campaignId}))
        }

    }, [campaignId]);

    useEffect(() => {
        if (decileAndSegmentData) {
            setDecileData(createDecileData(decileAndSegmentData.decile, counts?.deciles));
            setSegmentData(createSegmentData(decileAndSegmentData.segment, counts?.segments));
        }
        if(campaignId){
            if(counts){
                setDualDecileData(createIntensityAndCount(counts?.intensity, dualDeciles))
            }
            else if(counts === null || counts === undefined){
                setDualDecileData(createIntensityAndCount({}, dualDeciles))
            }
        }
       
    }, [decileAndSegmentData, counts, dualDeciles, campaignId]);

    useEffect(() => {
        if (campaign.id) {
            setValue(
                'trigger',
                campaign.triggers?.map((trigger) => ({
                    name: trigger.name,
                    id: trigger.id,
                    label: trigger.name,
                    value: trigger.id,
                }))
            );
            setValue('campaignName', campaign.name);
            setValue('targetingDuration', campaign?.targeted_duration);
            setValue('estimatedDuration', campaign?.estimated_duration);
            setValue('day', campaign.schedule);
            setValue('time', campaign.hour + 1);
            setValue('autoScheduler', campaign.auto_scheduler ? true : false);
        }
    }, [campaign]);

    useEffect(() => {
        getCampaignGenerationTypeData();
    }, []);

    const renderNoTableData = (text) => {
        return (
            <TableRow className='height-280px'>
                <TableCell colSpan={5}>
                    <Box textAlign="center" p={3}>
                        <Typography className="text-sm-regular gray-900">
                            No {text} data to display.
                        </Typography>
                    </Box>
                </TableCell>
            </TableRow>
        );
    };

    const NoDecileData = () => {
        return (
            <>
                {ROWS?.map(
                    (
                        row,
                        index
                    ) => (
                        <>
                            <TableRow
                                key={
                                    index
                                }
                                className="bg-gray-50 no-border"
                            >
                                <TableCell className="table-row-data no-border">
                                    {
                                        row.deciles
                                    }
                                </TableCell>
                                <TableCell className="table-row-data no-border">
                                    {
                                        row.quantity
                                    }
                                </TableCell>
                                <TableCell className="table-row-data no-border">
                                    {
                                        row.importance
                                    }
                                </TableCell>
                            </TableRow>
                        </>
                    )
                )}
            </>
        )
    };

    const decileTable = () => {
        return (
            <Box className="pb-24px">
                <TableContainer
                    component={Paper}
                    className="no-box-shadow no-border"
                >
                    <Table className="table-border-collapse no-border">
                        <TableHead
                            className="queries-table-heading"
                            sx={{
                                '& .MuiTableCell-root':
                                {
                                    borderBottom:
                                        'none',
                                },
                            }}
                        >
                            <TableRow className="bg-white no-border">
                                <TableCell className="table-headers-title">
                                    Deciles
                                </TableCell>
                                <TableCell className="table-headers-title">
                                    Quantity
                                </TableCell>
                                <TableCell className="table-headers-title">
                                    Importance
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {decileData?.length > 0 && campaignId? (
                                decileData?.map((row, index) => (
                                    <TableRow key={index} className="bg-gray-50 no-border">
                                        <TableCell className="table-row-data no-border">
                                            {row.decile}
                                        </TableCell>
                                        <TableCell className="table-row-data no-border">
                                            {row.physician_qty}
                                        </TableCell>
                                        <TableCell className="table-row-data no-border">
                                            {row.importance}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                NoDecileData
                            )}
                            <TableRow>
                                <TableCell className='text-sm-semi-bold gray-700' align='right' colSpan={2} > Total:</TableCell>
                                <TableCell className='text-sm-semi-bold gray-700 bg-primary-50' >{campaignId ? (counts?.total || 0): 0}</TableCell>
                            </TableRow>
                        </TableBody>

                    </Table>
                </TableContainer>
            </Box>
        )
    }

    const segmentTable = () => {
        return (
            <Box className="pb-24px mt-2">
                <TableContainer
                    component={Paper}
                    className="no-box-shadow no-border"
                >
                    <Table className="table-border-collapse no-border">
                        <TableHead
                            className="queries-table-heading"
                            sx={{
                                '& .MuiTableCell-root':
                                {
                                    borderBottom:
                                        'none',
                                },
                            }}
                        >
                            <TableRow className="bg-white no-border">
                                <TableCell className="table-headers-title">
                                    Segment
                                </TableCell>
                                <TableCell className="table-headers-title">
                                    Quantity
                                </TableCell>
                                <TableCell className="table-headers-title">
                                    Importance
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {segmentData?.length > 0 && campaignId ? (
                                segmentData?.map((row, index) => (
                                    <TableRow key={index} className="bg-gray-50 no-border">
                                        <TableCell className="table-row-data no-border">
                                            {row.segment}
                                        </TableCell>
                                        <TableCell className="table-row-data no-border">
                                            {row.physician_qty}
                                        </TableCell>
                                        <TableCell className="table-row-data no-border">
                                            {row.importance}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                NoDecileData
                            )}
                            <TableRow>
                                <TableCell className='text-sm-semi-bold gray-700' align='right' colSpan={2} > Total:</TableCell>
                                <TableCell className='text-sm-semi-bold gray-700 bg-primary-50' >{campaignId ? (counts?.total || 0): 0}</TableCell>
                            </TableRow>
                        </TableBody>

                    </Table>
                </TableContainer>
            </Box>
        )
    }

    const dualDecileTable = () => {
        return (
            <Box className="pb-24px mt-2">
                <TableContainer
                    component={Paper}
                    className="no-box-shadow no-border"
                >
                    <Table className="table-border-collapse no-border">
                        <TableHead
                            className="queries-table-heading"
                            sx={{
                                '& .MuiTableCell-root':
                                {
                                    borderBottom:
                                        'none',
                                },
                            }}
                        >
                            <TableRow className="bg-white no-border">
                                {DUAL_DECILE_TABLE_COLUMNS?.map(item => {
                                    return (
                                        <TableCell className="table-headers-title">
                                            {item.value}
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dualDecileData?.length > 0 ? (
                                dualDecileData?.map((row, index) => (
                                    <TableRow key={index} className="bg-gray-50 no-border">
                                        <TableCell className="table-row-data no-border">
                                            {row?.client_decile_importance}
                                        </TableCell>
                                        <TableCell className="table-row-data no-border">
                                            {row?.digital_affinity_importance}
                                        </TableCell>
                                        <TableCell className="table-row-data no-border">
                                            {row?.physician_qty}
                                        </TableCell>
                                        <TableCell className="table-row-data no-border">
                                            {row?.digital_intensity}
                                        </TableCell>
                                        <TableCell className="table-row-data no-border">
                                            {row?.promotion_intensity}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                NoDecileData
                            )}
                            <TableRow>
                                <TableCell className='text-sm-semi-bold gray-700' align='right' colSpan={2} > Total:</TableCell>
                                <TableCell className='text-sm-semi-bold gray-700 bg-primary-50' >{campaignId ? (counts?.total || 0): 0}</TableCell>
                            </TableRow>
                        </TableBody>

                    </Table>
                </TableContainer>
            </Box>
        )
    }

    const summaryTable = () => {
        if (!campaignsummary || Object.keys(campaignsummary).length === 0) {
            return renderNoTableData("summary");
        }

        const keys = Object.keys(campaignsummary);
        const headers = Object.keys(campaignsummary[keys[0]]);

        return (
            <Box className="pb-24px mt-2">
                <TableContainer
                    component={Paper}
                    className="no-box-shadow no-border"
                >
                    <Table className="table-border-collapse no-border">
                        <TableHead className="queries-table-heading"
                            sx={{
                                '& .MuiTableCell-root':
                                {
                                    borderBottom:
                                        'none',
                                },
                            }}>
                            <TableRow className="bg-white no-border">
                                <TableCell></TableCell>
                                {headers?.map((header) => (
                                    <TableCell key={header} className="text-capitalize table-headers-title">{header}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {keys?.map((key) => {
                                return (
                                    <TableRow key={key} className="bg-gray-50 no-border">
                                        <TableCell className="table-row-data no-border">{key === "1" ? 'First' : key === "2" ? 'Second' : key === "3" ? 'Third' : key === "4" ? 'Fourth' : ''} Order</TableCell>
                                        {headers?.map((header, index) => {
                                            const nestedValues = Object.values(campaignsummary[key][header]);
                                            const value = nestedValues.length > 0 ? nestedValues?.reduce((acc, curr) => acc + Number(curr), 0) : 0;
                                            return (
                                                <TableCell key={`${key}_${index}`} className="text-capitalize table-row-data no-border">{value}</TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };
    
    
    

    return (
        <Grid container className="custom-modal-inner-container">
            <form onSubmit={handleSubmit(onSubmit)} className="w-100">
                <Grid item xs={12}>
                    <Box className="padding-24px">
                        {/* Create new campaign */}
                        <Box className="flex-row-space-between pb-24px">
                            <Typography className="text-lg-medium gray-900">
                            {campaignId
                                    ? 'Edit Campaign'
                                    : 'Create new campaign'}
                            </Typography>
                            <Box className="icon" onClick={handleClose}>
                                <CloseIcon
                                    color="#98A2B3"
                                    width="12"
                                    height="12"
                                />
                            </Box>
                        </Box>

                        {/* Select Triggers */}
                        <Box className="pb-24px">
                            <CustomReactSelect
                                label="Select Trigger"
                                selectedOption={trigger}
                                options={campaignTriggers}
                                multipleSelect={true}
                                handleFilter={(option, actionMeta) => handleTriggerChange(option, actionMeta)}
                                placeholder="Select Triggers"
                                {...restTriggerProps}
                            />
                            <span className="text-xs-regular error-500">
                                {errors.trigger?.message}
                            </span>
                        </Box>

                        {/* Campaign Name */}
                        <Box className="flex-column gap-6 w-100 pb-24px">
                            <Typography className="text-sm-medium gray-500">
                                Campaign Name
                            </Typography>

                            <input
                                type="text"
                                placeholder="Campaign Name"
                                className="custom-text-field padding-tb-10-rl-14"
                                {...register('campaignName')}
                            />
                            <span className="text-xs-regular error-500">
                                {errors.campaignName?.message}
                            </span>
                        </Box>

                        {/* Targeting Duration */}
                        <Box className="pb-24px">
                            <CustomReactSelect
                                label="Targeting Duration"
                                selectedOption={
                                    targetingDuration
                                        ? targetingDurations.find(
                                              (x) => x.value === targetingDuration
                                          )
                                        : targetingDuration
                                }
                                options={targetingDurations}
                                handleFilter={(option) =>
                                    onTargetingDurationChange(
                                        option ? option.value : option
                                    )
                                }
                                placeholder="Select Targeting Duration"
                                {...restTargetingDurationProps}
                            />
                            <span className="text-xs-regular error-500">
                                {errors.targetingDuration?.message}
                            </span>
                        </Box>

                        {/* Estimated Duration */}
                        <Box className="pb-24px">
                            <CustomReactSelect
                                label="Select Estimated Duration"
                                selectedOption={
                                    estimatedDuration
                                        ? estimatedDurations.find(
                                              (x) => x.value === estimatedDuration
                                          )
                                        : estimatedDuration
                                }
                                options={estimatedDurations}
                                handleFilter={(option) =>
                                    onEstimatedDurationChange(
                                        option ? option.value : option
                                    )
                                }
                                placeholder="Select Estimated Duration"
                                {...restEstimatedDurationProps}
                            />
                            <span className="text-xs-regular error-500">
                                {errors.estimatedDuration?.message}
                            </span>
                        </Box>

                        {/* Duration Time and Day */}
                        { !autoScheduler && <>
                        <Box className="pb-24px">
                            <Grid
                                container
                                spacing={'24px'}
                                className="flex-row-end"
                            >
                                <Grid item xs={6}>
                                    <CustomReactSelect
                                        label="Export to Partner Every"
                                        options={daysOptions}
                                        placeholder="Select Day"
                                        selectedOption={
                                            day
                                                ? daysOptions.find(
                                                        (x) =>
                                                            x.value ===
                                                            day
                                                    )
                                                : day
                                        }
                                        handleFilter={(option) =>
                                            onDayChange(
                                                option
                                                    ? option.value
                                                    : option
                                            )
                                        }
                                        {...restDayProps}
                                        isSorted={true}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.day?.message}
                                    </span>
                                </Grid>
                                <Grid item xs={6}>
                                    <CustomReactSelect
                                        options={hourlyDropdownOptions}
                                        placeholder="Select Time"
                                        selectedOption={
                                            time
                                                ? hourlyDropdownOptions.find(
                                                        (x) =>
                                                            x.value ===
                                                            time
                                                    )
                                                : time
                                        }
                                        handleFilter={(option) =>
                                            onTimeChange(
                                                option
                                                    ? option.value
                                                    : option
                                            )
                                        }
                                        {...restTimeProps}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.time?.message}
                                    </span>
                                </Grid>
                            </Grid>
                        </Box> </> }

                        <Box className="min-width-50">
                            <CustomSwitch
                                label="Auto Scheduler"
                                checked={autoScheduler}
                                onChange={onAutoSchedulerChange}
                                {...restAutoSchedulerProps}
                            />
                        </Box>

                        <Divider className="border-1-gray" />

                        {/* Total Number of Physicians in this Campaign */}
                        <Box className="flex-row-space-between-center gap-16 padding-tb-24-rl-0">
                            <Typography className="text-sm-medium gray-700">
                                Total Number of Physicians in this
                                Campaign :
                            </Typography>
                            <Typography className="text-xl-medium gray-900">
                            {campaignId ? (counts?.total || 0): 0}
                            </Typography>
                        </Box>

                        <Divider className="border-1-gray" />
                      {/* Summary */}
                       {campaignId && <Box>
                            <Accordion
                                expanded={summary}
                                onChange={handleShowDualSummary}
                                className="no-box-shadow"
                            >
                                <AccordionSummary
                                    className="padding-0"
                                    expandIcon={
                                        <Box className="flex-column-center bg-white margin-10px br-50px border-1-gray-300">
                                            {summary ? (
                                                <Remove
                                                    color="#344054"
                                                    iconStyle={{
                                                        width: 20,
                                                        height: 20,
                                                    }}
                                                />
                                            ) : (
                                                <Add
                                                    color="#344054"
                                                    iconStyle={{
                                                        width: 20,
                                                        height: 20,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                >
                                    <Typography className="text-lg-medium gray-900">
                                        Summary
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails className="padding-0 flex-column gap-24 border-bottom-gray">
                                    {/* Dual Deciling */}
                                    {summaryTable()}
                                </AccordionDetails>
                            </Accordion>
                        </Box>}
                        

                        {/* Importance for Deciles and Segments */}
                        <Box>
                            <Accordion
                                expanded={decileAndSegment}
                                onChange={handleShowDecileAndSegment}
                                className="no-box-shadow"
                            >
                                <AccordionSummary
                                    className="padding-0"
                                    expandIcon={
                                        <Box className="flex-column-center bg-white margin-10px br-50px border-1-gray-300">
                                            {decileAndSegment ? (
                                                <Remove
                                                    color="#344054"
                                                    iconStyle={{
                                                        width: 20,
                                                        height: 20,
                                                    }}
                                                />
                                            ) : (
                                                <Add
                                                    color="#344054"
                                                    iconStyle={{
                                                        width: 20,
                                                        height: 20,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                >
                                    <Typography className="text-lg-medium gray-900">
                                        Importance for Deciles and
                                        Segments
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails className="padding-0 flex-column gap-24 border-bottom-gray">
                                    {/* Importance for Deciles and Segments */}
                                    {decileTable()}
                                    {segmentTable()}
                                </AccordionDetails>
                            </Accordion>
                        </Box>

                        {/* Dual Deciling */}
                        <Box>
                            <Accordion
                                expanded={dualDecile}
                                onChange={handleShowDualDecile}
                                className="no-box-shadow"
                            >
                                <AccordionSummary
                                    className="padding-0"
                                    expandIcon={
                                        <Box className="flex-column-center bg-white margin-10px br-50px border-1-gray-300">
                                            {dualDecile ? (
                                                <Remove
                                                    color="#344054"
                                                    iconStyle={{
                                                        width: 20,
                                                        height: 20,
                                                    }}
                                                />
                                            ) : (
                                                <Add
                                                    color="#344054"
                                                    iconStyle={{
                                                        width: 20,
                                                        height: 20,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                >
                                    <Typography className="text-lg-medium gray-900">
                                        Dual Deciling
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails className="padding-0 flex-column gap-24 border-bottom-gray">
                                    {/* Dual Deciling */}
                                    {dualDecileTable()}
                                </AccordionDetails>
                            </Accordion>
                        </Box>

                    </Box>
                </Grid>

                <Grid item xs={12} className="custom-modal-sticky-container">
                    <Divider className="border-1-gray" />
                    <Box className="flex-row-end-center gap-12 padding-tb-16-rl-24">
                        <CustomButton
                            title="Cancel"
                            handleClick={handleClose}
                        />
                        <CustomButton
                            title="Save"
                            titleColor="#FFFFFF"
                            buttonColor="#AD509E"
                            handleClick={handleSave}
                            type="submit"
                        />
                    </Box>
                </Grid>
            </form>
        </Grid>
    );
}

export default CreateAlgorithmCampaign;
